import { FC, useMemo } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { Table } from "@/components/elements/Table";
import { styled, Box } from "@mui/material";
import { divider } from "@/theme/colors";
import { truncateWithEllipsis } from "@/utils/string";
import { useEnum } from "@/api/services/global/enum";
import { findLabelByValue } from "@/utils/object";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { TJobListOutputDto } from "@/api/services/main/job/dtos/get-job-list.dto";

type TJobAfterImportProps = {
  rows: TJobListOutputDto[];
  isLoading: boolean;
};

export const JobAfterImport: FC<TJobAfterImportProps> = ({ rows, isLoading }) => {
  const { data: jobStatusOptions, isLoading: isStatusLoading } = useEnum("EJobStatus");
  const { data: recruitmentLocationTypeOptions, isLoading: isLocationLoading } = useEnum("ERecruitmentLocationType");
  const { data: japaneseLevelTypeOptions, isLoading: isJapaneseLoading } = useEnum("EJapaneseLevelType");

  const isTableLoading = isLoading || isStatusLoading || isLocationLoading || isJapaneseLoading;

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, 15);
  };

  const columns = useMemo<MRT_ColumnDef<TJobListOutputDto>[]>(
    () => [
      {
        id: "status",
        header: "ステータス",
        accessorKey: "status",
        Cell: ({ row }) => (
          <CenteredCell>
            <JobStatusTag status={row.original?.status} title={findLabelByValue(jobStatusOptions, row.original.status)} variant="bold" />
          </CenteredCell>
        ),
      },
      {
        id: "managementNumber",
        header: "求人管理番号",
        Cell: ({ row }) => truncateText(row.original?.managementNumber) ?? EMPTY_STR.TEXT,
      },
      {
        id: "companyName",
        header: "企業名",
        Cell: ({ row }) => truncateText(row.original?.company?.companyName) ?? EMPTY_STR.TEXT,
      },
      {
        id: "jobName",
        header: "求人タイトル",
        Cell: ({ row }) => truncateText(row.original?.jobName) ?? EMPTY_STR.TEXT,
      },
      {
        id: "recruiterId",
        header: "求人担当者",
        Cell: ({ row }) => truncateText(row.original?.recruiter?.fullName) ?? EMPTY_STR.TEXT,
      },
      {
        id: "companySpecificIndustryId",
        header: "分野",
        Cell: ({ row }) => truncateText(row.original?.companySpecificIndustry?.companySpecificIndustry) ?? EMPTY_STR.TEXT,
      },
      {
        id: "japaneseLevelType",
        header: "必要な日本語レベル",
        Cell: ({ row }) =>
          row.original.japaneseLevelType ? truncateText(findLabelByValue(japaneseLevelTypeOptions, row.original.japaneseLevelType)) : EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentNumber",
        header: "募集人数",
        Cell: ({ row }) => (row.original?.recruitmentNumber != null ? `${row.original.recruitmentNumber}名` : EMPTY_STR.TEXT),
      },
      {
        id: "workplace",
        header: "勤務場所",
        Cell: ({ row }) => truncateText(row.original?.workplace) ?? EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentLocationType",
        header: "募集地",
        Cell: ({ row }) =>
          row.original.recruitmentLocationType
            ? truncateText(findLabelByValue(recruitmentLocationTypeOptions, row.original.recruitmentLocationType))
            : EMPTY_STR.TEXT,
      },
    ],
    [jobStatusOptions, recruitmentLocationTypeOptions, japaneseLevelTypeOptions],
  );

  const { table } = useTable({
    columns,
    rows,
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
  });

  return (
    <TableWrapper>
      <Table table={table} isLoading={isTableLoading} borderColor={divider.high} height="calc(100vh - 620px)" headRowHeight={37} bodyRowHeight={42} />
    </TableWrapper>
  );
};

const TableWrapper = styled(Box)`
  .MuiTypography-root {
    padding: 0;
  }
`;
